import { useMemo, useState } from "react";

import { useKundeAkte } from "../../hooks/useKundeAktivitaet";
import { errorText } from "../../lib/invoices";
import {
  VERLAUF_FILTER,
  tagDe,
  type VerlaufEintrag,
  type VerlaufQuelle,
} from "../../lib/kundeAktivitaet";

/** Kurzform für die Chronik: `31.08.2026` bzw. `31.08.2026, 14:30` bei Terminen. */
function zeitpunktDe(wert: string, mitUhrzeit = false): string {
  if (!wert) return "";
  const tag = tagDe(wert.slice(0, 10));
  if (!mitUhrzeit || wert.length < 16) return tag;
  return `${tag}, ${wert.slice(11, 16)}`;
}

const QUELLE_MARKE: Record<VerlaufQuelle, string> = {
  dokument: "Beleg",
  termin: "Termin",
  projekt: "Projekt",
  post: "Post",
};

type Props = { kundeId: string };

/** So viele Einträge zeigt der Verlauf, bevor „Ganzen Verlauf zeigen" nötig ist. */
const ERSTE = 5;

/**
 * Verlauf des Kunden — Chronik aus Belegen, Terminen und Projektakte (siehe
 * `lib/kundeAktivitaet.ts`), nur gelesen.
 *
 * Die Post steht seit 04.10.2026 nicht mehr hier (Benjamin: „alles, was mit
 * Nachrichten zum Kunden zu tun hat, erreiche ich über den Knopf Nachrichten
 * oben"). Der Knopf öffnet den Chat des Kunden im Nachrichtenfenster.
 *
 * Seit 03.10.2026 ein Abschnitt der Kundenansicht (`KundeAnsicht`): die
 * Aufgaben & Notizen stehen dort als eigener Abschnitt oben, die Überschrift
 * trägt der Abschnitt. Gezeigt werden zuerst die jüngsten Einträge.
 */
export default function KundeAktivitaet({ kundeId }: Props) {
  const akte = useKundeAkte(kundeId);

  const [filter, setFilter] = useState<VerlaufQuelle | null>(null);
  const [alle, setAlle] = useState(false);
  const fehler = akte.error ? errorText(akte.error) : "";

  const zeitleiste: VerlaufEintrag[] = useMemo(
    () => (akte.data?.verlauf || [])
      .filter((e) => e.typ !== "post" && (!filter || e.typ === filter))
      .sort((a, b) => (a.zeitpunkt < b.zeitpunkt ? 1 : -1)),
    [akte.data, filter],
  );

  return (
    <div className="k-akte k-akte-im-abschnitt">
      {fehler && <p className="k-akte-fehler" role="alert">{fehler}</p>}

      <div className="k-akte-filter" role="group" aria-label="Verlauf filtern">
        {VERLAUF_FILTER.map((f) => (
          <button
            key={f.label}
            type="button"
            className={"k-akte-chip" + (filter === f.id ? " is-active" : "")}
            aria-pressed={filter === f.id}
            onClick={() => { setFilter(f.id); setAlle(false); }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {akte.isLoading && <p className="k-akte-leer">Wird geladen…</p>}
      {!akte.isLoading && zeitleiste.length === 0 && (
        <p className="k-akte-leer">
          {filter ? "In dieser Ansicht steht noch nichts." : "Noch nichts passiert."}
        </p>
      )}

      <ol className="k-akte-timeline">
        {(alle ? zeitleiste : zeitleiste.slice(0, ERSTE)).map((e) => {
          return (
            <li key={`${e.typ}-${e.id}`} className={"k-akte-zeile is-" + e.typ}>
              <span className="k-akte-punkt" aria-hidden="true" />
              <div className="k-akte-inhalt">
                <p className="k-akte-titel">{e.titel}</p>
                {e.text && <p className="k-akte-text">{e.text}</p>}
                <p className="k-akte-meta">
                  <span className="k-akte-marke">{e.akte_label && e.typ === "projekt" ? e.akte_label : QUELLE_MARKE[e.typ]}</span>
                  {zeitpunktDe(e.zeitpunkt, e.typ === "termin")}
                  {e.projekt_titel && ` · ${e.projekt_titel}`}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
      {zeitleiste.length > ERSTE && (
        <button type="button" className="lf-knopf k-akte-alle" aria-pressed={alle} onClick={() => setAlle((v) => !v)}>
          {alle ? "Weniger zeigen" : `Ganzen Verlauf zeigen (${zeitleiste.length})`}
        </button>
      )}
    </div>
  );
}
